function ttProductPreviewUrl(value) {
  const themeId = document.documentElement.dataset.ttPreviewTheme;
  if (!themeId) return value;
  const url = new URL(value, window.location.origin);
  if (url.origin === window.location.origin) url.searchParams.set('preview_theme_id', themeId);
  return url.toString();
}

if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      connectedCallback() {
        this.garrPreviewKey = new URLSearchParams(window.location.search).get("preview_key");
        this.initializeProductSwapUtility();
        this.quotePendingAbort = new AbortController();
        this.addEventListener('click', event => {
          if (!this.quoteSelectionPending || !event.target.closest('[data-product-quote-link], [data-schunk-quote]')) return;
          event.preventDefault();
          event.stopImmediatePropagation();
        }, { capture: true, signal: this.quotePendingAbort.signal });

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.quotePendingAbort?.abort();
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues, selectedVariantId } }) {
        if (!this.contains(event.target)) return;

        this.quoteSelectionPending = true;
        this.setAttribute('aria-busy', 'true');
        this.querySelectorAll('[data-product-quote-link], [data-schunk-quote]').forEach(link => link.setAttribute('aria-disabled', 'true'));
        this.resetProductFormState();
        if (selectedVariantId) this.toggleAddButton(true);

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage, selectedVariantId),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      get productForm() {
        return this.querySelector('form[is="product-form"');
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.resetFormState();
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          // Keep the authoritative product graph aligned with every settled variant.
          const schemaSource = html.querySelector('script[data-tt-product-schema]');
          const schemaDestination = this.closest('.shopify-section')?.querySelector('script[data-tt-product-schema]');
          if (schemaSource && schemaDestination) schemaDestination.textContent = schemaSource.textContent;

          this.updateMedia(variant);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Barcode', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Badges', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('PricePerItem', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Volume');
          // Keep the SDS request attached to the selected container/SKU.
          const sdsSource = html.querySelector('[data-product-sds]');
          const sdsDestination = this.querySelector('[data-product-sds]');
          if (sdsSource && sdsDestination) sdsDestination.href = sdsSource.href;

          const purchaseSource = html.querySelector('[data-tt-purchase-controls][data-tt-mixed-quotes]');
          const purchaseDestination = this.querySelector('[data-tt-purchase-controls][data-tt-mixed-quotes]');
          if (purchaseSource && purchaseDestination) {
            const quantity = purchaseDestination.querySelector('[name="quantity"]')?.value || '1';
            purchaseDestination.innerHTML = purchaseSource.innerHTML;
            const quantityInput = purchaseDestination.querySelector('[name="quantity"]');
            if (quantityInput && /^[1-9]\d*$/.test(quantity)) {
              quantityInput.value = quantity;
              quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
            }
            const notes = this.querySelectorAll('[data-tt-commercial-note]');
            html.querySelectorAll('[data-tt-commercial-note]').forEach((source, index) => {
              if (notes[index]) notes[index].innerHTML = source.innerHTML;
            });
            this.currentVariant = variant;
            document.dispatchEvent(new CustomEvent('variant:changed', { detail: { variant } }));
            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
              data: { sectionId: this.sectionId, html, variant },
            });
            return;
          }

          const quoteForm = this.querySelector('form[data-quote-only="true"]');
          const quoteSource = html.querySelector('form[data-quote-only="true"]');
          if (quoteForm && quoteSource) {
            const quantity = quoteForm.querySelector('[name="quantity"]')?.value || '1';
            quoteForm.innerHTML = quoteSource.innerHTML;
            const input = quoteForm.querySelector('[name="quantity"]');
            if (input && /^[1-9]\d*$/.test(quantity)) {
              input.value = quantity;
              input.dispatchEvent(new Event('input', { bubbles: true }));
            }
            this.currentVariant = variant;
            document.dispatchEvent(new CustomEvent('variant:changed', { detail: { variant } }));
            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, { data: { sectionId: this.sectionId, html, variant } });
            return;
          }

          this.updateQuantityRules(this.sectionId, this.productId, html);
          this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('hidden');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

          const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.toggleAddButton(
            addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
            FoxTheme.variantStrings.soldOut
          );

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: this.currentVariant,
              },
            })
          );

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false, selectedVariantId) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (selectedVariantId && this.garrPreviewKey) {
          url = "/products_preview";
          params.push(`preview_key=${encodeURIComponent(this.garrPreviewKey)}`);
        }
        if (selectedVariantId) {
          params.push(`variant=${encodeURIComponent(selectedVariantId)}`);
        } else if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return ttProductPreviewUrl(`${url}?${params.join('&')}`);
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const requestController = this.abortController;
        const requestRevision = this.quoteRequestRevision = (this.quoteRequestRevision || 0) + 1;

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then(async (responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            if (this.querySelector('[data-merch-variant-config]')) {
              const variant = this.getSelectedVariant(html);
              await this.querySelector('media-gallery')?.prepareVariantMedia?.(variant);
            }
            if (requestController.signal.aborted || requestController !== this.abortController) return;
            this.pendingRequestUrl = null;
            callback(html);
            if (requestRevision === this.quoteRequestRevision) {
              this.quoteSelectionPending = false;
              this.removeAttribute('aria-busy');
              this.querySelectorAll('[data-product-quote-link], [data-schunk-quote]').forEach(link => link.removeAttribute('aria-disabled'));
            }
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        const preview = this.garrPreviewKey ? `/products_preview?preview_key=${encodeURIComponent(this.garrPreviewKey)}${variantId ? `&variant=${variantId}` : ''}` : `${url}${variantId ? `?variant=${variantId}` : ''}`;
        window.history.replaceState({}, '', ttProductPreviewUrl(preview));
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return; // Early return if productMedia is not found

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            productMedia.init();
            productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.innerHTML = FoxTheme.variantStrings.addToCart;
        }

        if (!modifyClass) return;
      }

      setUnavailable() {
        const form = document.getElementById(`product-form-${this.dataset.section}`);
        const variantInput = form?.elements.namedItem('id');
        if (variantInput) variantInput.value = '';
        this.toggleAddButton(true, FoxTheme.variantStrings.unavailable);
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        if (price) price.classList.add('hidden');
        if (inventory) inventory.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(ttProductPreviewUrl(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`))
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }
    }
  );
}

if (!customElements.get('product-promotion-alert')) {
  customElements.define(
    'product-promotion-alert',
    class ProductPromotionAlert extends HTMLElement {
      constructor() {
        super();

        this.abortController = new AbortController();
        this.buttonEl = this.querySelector('button');
        this.buttonEl.addEventListener('click', this.onClick.bind(this), {
          signal: this.abortController.signal,
        });
      }

      onClick(evt) {
        evt.preventDefault();

        this.closest('.product__block').classList.add('hidden');
      }

      disconnectedCallback() {
        this.abortController.abort();
      }
    }
  );
}


